export const getLEDGuardKey = (devicestring: string) =>
  `ledTurnedOn_${devicestring}`;

export const getLEDCacheKey = (devicestring: string) =>
  `ledSensorToggle_${devicestring}`;

export interface LEDCache {
  state: boolean;
  timestamp: number;
  devicestring: string;
  humanReadable: Date;
}

const STALE_TTL_MS = 86_400_000; // 24 hours
const PER_SENSOR_TTL_MS = 120_000; // 2 minutes
const LED_PREFIXES = ["ledTurnedOn_", "ledSensorToggle_", "ledPerSensor_"];

const isLEDKey = (key: string) => 
  LED_PREFIXES.some((prefix) => key.startsWith(prefix));

const isStaleTimestamp = (raw: string): boolean => {
  const { timestamp } = JSON.parse(raw);
  return typeof timestamp === "number" && Date.now() - timestamp > STALE_TTL_MS;
};

export const clearAllLEDKeys = () => {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && isLEDKey(key)) {
      localStorage.removeItem(key);
    }
  }
};

export const cleanStaleLEDKeys = () => {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key || !isLEDKey(key)) continue;

    try {
      const raw = localStorage.getItem(key);
      if (raw && isStaleTimestamp(raw)) {
        localStorage.removeItem(key);
      }
    } catch {
      localStorage.removeItem(key); // If the data cannot be parsed (in the try block), then delete the key anyway
    }
  }
};

export const setCachedLEDState = (devicestring: string, state: boolean) => {
  const cache: LEDCache = {
    state,
    timestamp: Date.now(),
    devicestring,
    humanReadable: new Date(),
  };
  localStorage.setItem(getLEDCacheKey(devicestring), JSON.stringify(cache));
};

// -- Per-sensor LED cache (2-minute TTL, used in Devices tab) --

export const getPerSensorLEDCacheKey = (devicestring: string, sensorId: string) =>
  `ledPerSensor_${devicestring}_${sensorId}`;

export const setPerSensorLEDCache = (devicestring: string, sensorId: string, state: boolean) => {
  localStorage.setItem(
    getPerSensorLEDCacheKey(devicestring, sensorId),
    JSON.stringify({ state, timestamp: Date.now() })
  );
};

export const getPerSensorLEDCache = (devicestring: string, sensorId: string): boolean | null => {
  const key = getPerSensorLEDCacheKey(devicestring, sensorId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { state, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < PER_SENSOR_TTL_MS) {
      return state;
    }
  } catch {
    localStorage.removeItem(key);
  }
  return null;
};
